import { useEffect, useRef } from "react";
import { CanvasDrawer } from "../CanvasDrawer";
import { useStickerSectionStore } from "../EditorStore";
import useTwoFingerPanAndZoom from "./CanvasFeaturesCustomHooks/useTwoFingerPanAndZoom";
import usePan from "./CanvasFeaturesCustomHooks/usePan";
import useSelector from "./CanvasFeaturesCustomHooks/useSelector";
import { phoneTemplateImages } from "../utils/phoneDetails";

export default function CanvasSection() {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const hintOverlayRef = useRef<HTMLDivElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    const { twoFingerTouchStart, twoFingerTouchMove, twoFingerTouchEnd } = useTwoFingerPanAndZoom();
    const { startSingleFingerPan, moveSingleFingerPan, endSingleFingerPan, startMousePan, moveMousePan, endMousePan } = usePan();
    const { startSelect, selectMove, startSelectMouse, selectMoveMouse, endSelect } = useSelector();

    const phoneModel = useStickerSectionStore((state) => state.phoneModel);

    const toolSelectedRef = useStickerSectionStore((state) => state.toolSelectedRef);
    const touchTimerRef = useRef<NodeJS.Timeout | null>(null);
    const selectReadyRef = useRef(true)

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (overlayRef.current) {
                overlayRef.current.style.opacity = "0";
                overlayRef.current.style.pointerEvents = "none";
            }
        }, 0);
        return () => clearTimeout(timeout);
    }, []);

    const dismissedRef = useRef(false);

    useEffect(() => {
        const checkDismiss = () => {
            const { hasPinched, hasZoomed } = useStickerSectionStore.getState();
            if (hasPinched && hasZoomed && hintOverlayRef.current) {
                dismissedRef.current = true;
                hintOverlayRef.current.style.opacity = "0";
                hintOverlayRef.current.style.pointerEvents = "none";
            }
        };
        const unsubPinch = useStickerSectionStore.subscribe((s) => s.hasPinched, checkDismiss);
        const unsubZoom = useStickerSectionStore.subscribe((s) => s.hasZoomed, checkDismiss);

        const updateVisibility = (tool: string) => {
            if (!hintOverlayRef.current || dismissedRef.current) return;
            hintOverlayRef.current.style.opacity = tool === "selector" ? "1" : "0";
        };

        updateVisibility(useStickerSectionStore.getState().toolSelected);

        const unsubTool = useStickerSectionStore.subscribe(
            (s) => s.toolSelected,
            updateVisibility
        );

        return () => { unsubPinch(); unsubZoom(); unsubTool(); };
    }, []);

    const initializeCanvas = async (canvas: HTMLCanvasElement) => {
        if (!canvas) return;

        canvasRef.current = canvas;

        const ctx = canvas.getContext("2d");
        if (ctx) {
            CanvasDrawer.canvas = canvas;
            CanvasDrawer.ctx = ctx;

            CanvasDrawer.initializeCanvasResolution();
            CanvasDrawer.initializeWaterMark();
            if (phoneModel)
                await CanvasDrawer.initializePhoneTemplate(phoneTemplateImages[phoneModel]!.template);

            CanvasDrawer.initializeGhostImageArray()
            CanvasDrawer.allocateGhostArrayImages()
            CanvasDrawer.rerenderCanvas();
        }
    };

    useEffect(() => {

        const handleResize = () => {
            if (!canvasRef.current) return;
            CanvasDrawer.initializeCanvasResolution();
            CanvasDrawer.rerenderCanvas();
            window.location.reload();

        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleFocus = () => {
            CanvasDrawer.rerenderCanvas();
        };

        window.addEventListener("focus", handleFocus);
        return () => window.removeEventListener("focus", handleFocus);
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current

        if (!canvas) return;

        const unsub = useStickerSectionStore.subscribe(
            (state) => state.cursorStyle,
            (cursorStyle) => {
                canvas.style.cursor = cursorStyle
            }
        );

        return () => unsub();
    }, [])

    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        const touches = e.touches.length;

        if (touches === 1) {
            touchTimerRef.current = setTimeout(() => {
                if (touchTimerRef.current) {
                    touchTimerRef.current = null;

                    if (toolSelectedRef.current === "pan" || toolSelectedRef.current === "background") {
                        startSingleFingerPan(e);
                    } else if (toolSelectedRef.current === "selector") {
                        startSelect(e);
                    }
                }
            }, 25);
        }

        if (touches === 2) {
            touchTimerRef.current = null;
            twoFingerTouchStart(e);
        }
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        if (!touchTimerRef.current && selectReadyRef.current) {
            if (e.touches.length === 1) {
                if (toolSelectedRef.current === "pan" || toolSelectedRef.current === "background") {
                    moveSingleFingerPan(e);
                } else if (toolSelectedRef.current === "selector") {
                    selectMove(e);
                }
            }

            if (e.touches.length === 2) {
                twoFingerTouchMove(e);
            }
        }
    };

    const handleTouchEnd = () => {
        touchTimerRef.current = null;
        twoFingerTouchEnd();
        endSingleFingerPan();
        endSelect();
        selectReadyRef.current = false
        setTimeout(() => {
            selectReadyRef.current = true
        }, 50)
    };

    const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if ((toolSelectedRef.current === "pan" || toolSelectedRef.current === "background") && canvasRef.current) {
            canvasRef.current.style.cursor = "grabbing"
            startMousePan(e);
        } else if (toolSelectedRef.current === "selector") {
            startSelectMouse(e);
        }
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (toolSelectedRef.current === "pan" || toolSelectedRef.current === "background") {
            moveMousePan(e);
        } else if (toolSelectedRef.current === "selector") {
            selectMoveMouse(e);
        }
    };

    const handleMouseUp = () => {
        if ((toolSelectedRef.current === "pan" || toolSelectedRef.current === "background") && canvasRef.current) {
            canvasRef.current.style.cursor = "grab"
        }

        endMousePan();
        endSelect();
    };

    const handleMouseLeave = () => {
        endMousePan();
        endSelect();
    };

    return (
        <div
            style={{
                height: "100%",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                position: "relative",
            }}
        >
            <canvas
                id="canvas"
                ref={initializeCanvas}
                style={{
                    backgroundColor: "#3d5a5a",
                    height: "100%",
                    width: "100%",
                    WebkitUserSelect: "none",
                    userSelect: "none",
                    WebkitTouchCallout: "none",
                    WebkitTapHighlightColor: "transparent",
                    touchAction: "manipulation",
                    cursor: "grab"
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseLeave}
                tabIndex={0}
            />

            <div
                ref={overlayRef}
                style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundColor: "black",
                    zIndex: 540,
                    opacity: 1,
                    transition: "opacity 1.5s ease-in-out",
                    pointerEvents: "auto",
                }}
            />

            <div
                ref={hintOverlayRef}
                style={{
                    position: "absolute",
                    top: "12%",
                    right: "5%",
                    zIndex: 10,
                    opacity: 0,
                    transition: "opacity 0.8s ease-in-out",
                    pointerEvents: "none",
                }}
            >
                <img
                    src="/pinchandzoomanimation.png"
                    style={{

                        width: "min(35vw, 120px)",
                        borderRadius: "12px",
                    }}
                />
            </div>
        </div>
    );
}
